import { Button } from '@progress/kendo-react-buttons';
import { IconThemeColor, SvgIcon } from '@progress/kendo-react-common';
import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import React from 'react';
import { FileSystemExtendedAvailable } from '../../common/helpers';
import { TrashOutline, WarningTriangleOutline } from '../../icons/icons';
import { getDirectoryDb } from '../../services/directory-db';
import clsx from 'clsx';

export interface FilePickerProps {
    readonly indexedDbKey: string;
    title?: React.ReactNode;
    placeholder?: string;
    className?: string;
    onChange?: () => void;
}

export default function FilePicker(props: FilePickerProps) {
    const [handle, setHandle] = React.useState<FileSystemFileHandle | null>(null);
    const [permission, setPermission] = React.useState<PermissionState>('granted');

    React.useEffect(() => {
        const load = async () => {
            const db = await getDirectoryDb();
            const handle = await db.get('file-handles', props.indexedDbKey);
            if (handle && handle.kind === 'file') {
                setHandle(handle);
                setPermission(await handle.queryPermission({ mode: 'read' }));
            }
        };
        load();
    }, []);

    const handleEdit = async () => {
        const files = (await window.showOpenFilePicker({
            multiple: false
        })) as FileSystemFileHandle[];

        const file = files[0];
        if (!file) {
            return;
        }

        setHandle(file);
        const permission = await file.queryPermission({ mode: 'read' });
        setPermission(permission);
        if (permission !== 'granted') {
            setPermission(await file.requestPermission({ mode: 'read' }));
        }
        const db = await getDirectoryDb();
        db.put('file-handles', file, props.indexedDbKey);
        props.onChange?.();
    };

    const handleRemovePath = async () => {
        const db = await getDirectoryDb();
        db.delete('file-handles', props.indexedDbKey);
        setHandle(null);
        setPermission('granted');
    };

    const handleRequestPermission = async () => {
        setPermission(await handle!.requestPermission({ mode: 'read' }));
    };

    return (
        <div className={clsx('py-2 w-100', props.className)}>
            <div className="d-flex gap-2 w-100 align-items-center">
                <div className="flex-shrink-0">{props.title}</div>
                <TextBox
                    disabled={!FileSystemExtendedAvailable}
                    onClick={handleEdit}
                    className="flex-grow-1 cursor-pointer"
                    value={handle ? handle.name : ''}
                    readOnly={true}
                    prefix={() => (
                        <InputPrefix>
                            {permission !== 'granted' && (
                                <SvgIcon icon={WarningTriangleOutline} size="large" themeColor={permissionToColor(permission)}></SvgIcon>
                            )}
                        </InputPrefix>
                    )}
                    suffix={() => (
                        <InputSuffix>
                            {handle && (
                                <Button
                                    svgIcon={TrashOutline}
                                    fillMode={'outline'}
                                    themeColor={'error'}
                                    onClick={handleRemovePath}></Button>
                            )}
                        </InputSuffix>
                    )}
                    placeholder={props.placeholder || 'Click to set file'}></TextBox>
                {permission === 'prompt' && (
                    <div>
                        <Button fillMode={'outline'} themeColor={'primary'} onClick={handleRequestPermission}>
                            Grant permission
                        </Button>
                    </div>
                )}
            </div>
            {!FileSystemExtendedAvailable && (
                <div className="d-flex align-items-center secondary-text pt-2">
                    <SvgIcon icon={WarningTriangleOutline} size="large"></SvgIcon>
                    Feature unavailable in this browser
                </div>
            )}
        </div>
    );
}

function permissionToColor(permission: PermissionState): IconThemeColor {
    return permission === 'denied' ? 'error' : permission === 'prompt' ? 'warning' : 'inherit';
}
